import { formatNumber } from "$root/localization/formatters";
import { formatCurrencyAmount } from "$root/utils/currency";
import type { Currencies } from "@eu.mdotm.api/portfolio-lab";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import type { ICellRendererParams } from "ag-grid-community";
import { PortfolioLabContext } from "../../../ctx";
import type { MergedCompositionRow } from "../rows";
import { toClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { isWithinTolerance } from "@mdotm/utils/math";
import { labSum, truncateToPrecision } from "../../../utils/math";

type LabTotalsMonetaryCellRendererProps = ICellRendererParams<MergedCompositionRow> & {
	uuid: string;
	currency: Currencies | null;
};

/**
 * Self-subscribing totals cell — see `LabTotalsWeightCellRenderer` for why the pinned row's own
 * `totalMonetaryWeight` cannot be read here. With no AUM the monetary total is 0: the monetary
 * weights are AUM-derived, so there is nothing to add up. The total is a plain number, the column
 * header names the currency; the AUM it is checked against carries the code.
 */
export function LabTotalsMonetaryCellRenderer({ uuid, currency }: LabTotalsMonetaryCellRendererProps) {
	const ctx = useMandatoryContext(PortfolioLabContext);
	const investment = useAtomValue(ctx.investments$).find((i) => i.virtualUuid === uuid);
	if (!investment) {
		return <div />;
	}
	const { aum } = investment;
	const monetary = aum !== null ? labSum(investment.currentComposition, (c) => c.monetaryWeight) : 0;
	const withinTolerance = isWithinTolerance(aum ?? 0, monetary, 0.01);

	return (
		<div
			data-qualifier="PortfolioLab/Totals/Monetary"
			data-mismatch={!withinTolerance}
			className={toClassName({
				"flex flex-col justify-center leading-tight h-full text-right": true,
				"pr-3": withinTolerance,
			})}
		>
			<span
				className={toClassName({
					"font-medium": true,
					"text-[color:var(--palette-D500)]": !withinTolerance,
				})}
			>
				{formatNumber(truncateToPrecision(monetary, 2), 2)}
			</span>
			{aum != null && !withinTolerance && (
				<span className="text-xs text-(--palette-N400)">
					AUM {formatCurrencyAmount(truncateToPrecision(aum, 2), currency)}
				</span>
			)}
		</div>
	);
}
